import { Resources, useResource } from "Infrastructure/Translation/Resources";
import {
	getOrderFieldsEnumOptionsSchema,
	type OrderFieldsEnumOptionsFormModel,
} from "Settings/Modules/OrderSetup/Components/Form/Schema/orderFieldsEnumOptionsFormSchema";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import { zodResolver } from "@hookform/resolvers/zod";
import Close from "@mui/icons-material/Close";
import Delete from "@mui/icons-material/Delete";
import MoreVert from "@mui/icons-material/MoreVert";
import { Box, Grid2 } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { Fragment, useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";

type Props = {
	values?: string;
	onAccept: (value?: string) => void;
	formDisabled?: boolean;
	title?: string;
};

//TODO[JAKUB]: Move this somewhere else
export type OrderFieldValue = {
	value: string;
	label: string;
	emailLabel?: string;
};

const formDefaultValue = {
	value: "",
	label: "",
	emailLabel: "",
};

const orderSetupResource = Resources.base.orderSetup.editForm;

export const OrderFieldOptionsModal = ({
	values,
	onAccept,
	formDisabled,
	title,
}: Props) => {
	const { t } = useResource();
	const [isOpen, setIsOpen] = useState(false);

	const form = useForm<OrderFieldsEnumOptionsFormModel>({
		resolver: zodResolver(getOrderFieldsEnumOptionsSchema()),
		defaultValues: {
			options: [formDefaultValue],
		},
		mode: "onChange",
	});

	const {
		control,
		formState: { errors, isValid },
	} = form;

	const { fields, append, remove, replace } = useFieldArray({
		control,
		name: "options",
	});

	useEffect(() => {
		replace(values ? JSON.parse(values) : [formDefaultValue]);
	}, [values]);

	const handleAddOption = () => {
		append(formDefaultValue);
	};

	const handleAccept = () => {
		onAccept(
			form.getValues("options")
				? JSON.stringify(form.getValues("options"))
				: undefined,
		);
		setIsOpen(false);
	};

	const handleCancel = () => {
		setIsOpen(false);
	};

	return (
		<>
			<Box>
				<Button
					color="primary"
					onClick={() => setIsOpen(true)}
					sx={{ flexGrow: 1 }}
					startIcon={<MoreVert />}
				>
					{title ? title : t(orderSetupResource.optionsTitle)}
				</Button>
			</Box>
			<Dialog open={isOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
				<DialogTitle>
					{title ? title : t(orderSetupResource.optionsTitle)}
				</DialogTitle>
				<DialogContent sx={{ pb: 1 }}>
					<DialogContentText>
						{t(orderSetupResource.optionsDescription)}
					</DialogContentText>
					{fields.map((field, index) => (
						<Fragment key={field.id}>
							<Grid2 container columnSpacing={2}>
								<Grid2 size={{ xs: 12, sm: 4 }}>
									<Input
										id={`options.${index}.value`}
										name={`options.${index}.value`}
										label={t(orderSetupResource.value)}
										control={control}
										errors={errors}
										variant="outlined"
										disabled={formDisabled}
										fullWidth
									/>
								</Grid2>
								<Grid2 size={{ xs: 12, sm: 4 }}>
									<Input
										id={`options.${index!}.label`}
										name={`options.${index!}.label`}
										label={t(orderSetupResource.label)}
										control={control}
										errors={errors}
										variant="outlined"
										disabled={formDisabled}
										fullWidth
									/>
								</Grid2>
								<Grid2 size={{ xs: 12, sm: 4 }}>
									<Input
										id={`options.${index}.emailLabel`}
										name={`options.${index}.emailLabel`}
										label="Email Label"
										control={control}
										errors={errors}
										variant="outlined"
										disabled={formDisabled}
										fullWidth
									/>
								</Grid2>
							</Grid2>
							{!formDisabled && (
								<Button
									onClick={() => remove(index)}
									color="error"
									sx={{ mt: 1 }}
									startIcon={<Delete />}
								>
									{t(orderSetupResource.removeOption)}
								</Button>
							)}
						</Fragment>
					))}
					<Box>
						{!formDisabled && (
							<Button
								onClick={handleAddOption}
								variant="contained"
								color="primary"
								sx={{ mt: 1 }}
							>
								{t(orderSetupResource.addOption)}
							</Button>
						)}
					</Box>
				</DialogContent>
				<DialogActions sx={{ justifyContent: "center" }}>
					{!formDisabled && (
						<Button color="success" onClick={handleAccept} disabled={!isValid}>
							{t(orderSetupResource.saveOptions)}
						</Button>
					)}
					<Button startIcon={<Close />} onClick={handleCancel} color="error">
						{t(orderSetupResource.cancel)}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
